import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { URLS } from 'commons/app.constant';
import * as R from 'runtypes';
import { ApiHeldFunds } from 'core';
import moment, { Moment } from 'moment';

export enum DepositType {
  Full = 'FULL',
  Percentage = 'PERCENTAGE',
  Amount = 'AMOUNT',
}

export type FullDeposit = {
  type: DepositType.Full;
};
export type PercentageDeposit = {
  type: DepositType.Percentage;
  percentage: string;
};
export type AmountDeposit = {
  type: DepositType.Amount;
  amount: string;
};

export type DirectDepositAccount = {
  accountId: string;
  bank: {
    name: string;
    routingNum: string;
  };
  transactionLimit: number;
};

export type DirectDeposit = {
  employerName: string;
  deposit: FullDeposit | PercentageDeposit | AmountDeposit;
  signature: string;
};

export const ApiWireInstructions = R.Record({
  recipientName: R.String,
  recipientAddress: R.String,
  accountNumber: R.String,
  routingNumber: R.String.nullable(),
  swiftCode: R.String.nullable(),
  bankName: R.String,
  bankAddress: R.String,
  specialInstructions: R.String.nullable(),
  intermediaryBank: R.String.nullable(),
  depositReference: R.String.nullable(),
  minWireDeposit: R.Number.nullable(),
  sourceOfFundsCaptured: R.Boolean,
  sourceOfFundsUrl: R.String,
}).asReadonly();
export type WireInstructions = R.Static<typeof ApiWireInstructions>;

export const ApiWireWithdrawalMetadata = R.Record({
  minWireWithdrawal: R.Number.nullable(),
  maxDailyWireWithdrawal: R.Number.nullable(),
}).asReadonly();
export type WireWithdrawalMetadata = R.Static<typeof ApiWireWithdrawalMetadata>;

@Injectable({
  providedIn: 'root',
})
export class DepositService {
  private readonly http = inject(HttpClient);

  private sourceOfFundsShownAt: Moment | null = null;

  needFundsLink(instructions: WireInstructions): boolean {
    if (instructions.sourceOfFundsCaptured) {
      return false;
    } else if (this.sourceOfFundsShownAt != null) {
      const cuttoff = moment().subtract(10, 'minute');
      return this.sourceOfFundsShownAt.isBefore(cuttoff);
    } else {
      return true;
    }
  }

  fundsLinkClicked() {
    this.sourceOfFundsShownAt = moment();
  }

  getWireInstructions(): Observable<WireInstructions> {
    return this.http.get(URLS.wireDepositInstructionsUrl).pipe(map(ApiWireInstructions.check));
  }

  getWireWithdrawalMetadata(): Observable<WireWithdrawalMetadata> {
    return this.http.get(URLS.wireWithdrawalMetadataUrl).pipe(map(ApiWireWithdrawalMetadata.check));
  }

  getDirectDepositAccount(): Observable<DirectDepositAccount> {
    return this.http.get<DirectDepositAccount>(URLS.directDepositAccount);
  }

  saveDirectDeposit(deposit: DirectDeposit): Observable<Blob> {
    return this.http
      .post(URLS.directDeposit, deposit, { responseType: 'arraybuffer' })
      .pipe(map((body) => new Blob([body], { type: 'application/pdf' })));
  }

  getHoldFundsData(): Observable<Array<ApiHeldFunds>> {
    return this.http.get(URLS.holdFundsUrl).pipe(map(R.Array(ApiHeldFunds).check));
  }
}
